import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  FiHome, FiUsers, FiBriefcase, FiClipboard, FiSettings,
  FiMenu, FiX, FiLogOut, FiInbox, FiSun, FiMoon, FiChevronUp, FiCheck
} from 'react-icons/fi';
import NotificationBell from './NotificationBell';
import ProfileDropdown from './ProfileDropdown';
import caycoLogo from '../assets/Cayco_logo.png';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, fetchOrganizations, switchOrganization } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Home', permission: 'jobs.view' },
    { path: '/inbox', icon: FiInbox, label: 'Inbox', permission: 'inbox.view' },
    { path: '/jobs', icon: FiBriefcase, label: 'Jobs', permission: 'jobs.view' },
    { path: '/work', icon: FiClipboard, label: 'Work', permission: 'work.view' },
    { path: '/customers', icon: FiUsers, label: 'CRM', permission: 'customers.view' },
  ].filter(item => hasPermission(item.permission));

  useEffect(() => {
    let cancelled = false;
    const loadOrgs = async () => {
      const result = await fetchOrganizations();
      if (!cancelled && result.success) {
        setOrganizations(result.organizations);
      }
    };
    loadOrgs();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId, user?.organizationId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-org-switcher]')) {
        setOrgMenuOpen(false);
      }
    };
    if (orgMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [orgMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchOrg = async (organizationId) => {
    if (switchingOrg) return;
    if (organizationId === user?.organizationId) {
      setOrgMenuOpen(false);
      return;
    }
    setSwitchingOrg(true);
    const result = await switchOrganization(organizationId);
    setSwitchingOrg(false);
    setOrgMenuOpen(false);
    if (result.success) {
      navigate('/dashboard', { replace: true });
      window.location.reload();
    }
  };

  const currentOrgName = user?.companyName
    || organizations.find((o) => o.isCurrent)?.name
    || 'Organization';

  const OrgSwitcher = ({ compact = false }) => {
    if (!organizations.length) return null;

    return (
      <div className={`relative ${compact ? '' : 'mb-3'}`} data-org-switcher>
        <button
          type="button"
          onClick={() => setOrgMenuOpen((open) => !open)}
          disabled={switchingOrg}
          className={`w-full flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/60 hover:border-primary-400 dark:hover:border-primary-500 transition-colors disabled:opacity-60 ${
            compact || sidebarOpen ? 'px-3 py-2.5' : 'px-2 py-2 justify-center'
          }`}
          title="Switch organization"
        >
          <span className="flex-shrink-0 h-8 w-8 rounded-md bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-xs font-bold uppercase">
            {(currentOrgName || 'O').slice(0, 2)}
          </span>
          {(compact || sidebarOpen) && (
            <>
              <span className="flex-1 min-w-0 text-left">
                <span className="block text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {currentOrgName}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.role || 'Member'}
                  {organizations.length > 1 ? ' · Switch' : ''}
                </span>
              </span>
              {organizations.length > 1 && (
                <FiChevronUp className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${orgMenuOpen ? '' : 'rotate-180'}`} />
              )}
            </>
          )}
        </button>

        {orgMenuOpen && organizations.length > 1 && (
          <div className="absolute bottom-full left-0 right-0 mb-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg z-50">
            {organizations.map((org) => {
              const isCurrent = org.organizationId === user?.organizationId || org.isCurrent;
              return (
                <button
                  key={org.organizationId}
                  type="button"
                  disabled={switchingOrg}
                  onClick={() => handleSwitchOrg(org.organizationId)}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-2 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-60 ${
                    isCurrent ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''
                  }`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                      {org.name}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                      {org.role}
                    </span>
                  </span>
                  {isCurrent && <FiCheck className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navbar */}
      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 fixed w-full top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {mobileMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
              </button>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:block p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 ml-2"
              >
                <FiMenu className="h-6 w-6" />
              </button>
              <Link to="/dashboard" className="ml-4 flex items-center">
                <img
                  src={caycoLogo}
                  alt="Cayco"
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? (
                  <FiMoon className="h-5 w-5" />
                ) : (
                  <FiSun className="h-5 w-5" />
                )}
              </button>
              <NotificationBell />
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </nav>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'w-64' : 'w-16'
          } hidden lg:flex lg:flex-col fixed h-[calc(100vh-4rem)] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300`}
        >
          <nav className="mt-5 px-2 space-y-1 flex-1 overflow-y-auto pb-4">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${sidebarOpen ? 'mr-3' : 'mx-auto'}`} />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              );
            })}
            {hasPermission('users.view') && (
              <Link
                to="/settings"
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === '/settings'
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FiSettings className={`h-5 w-5 ${sidebarOpen ? 'mr-3' : 'mx-auto'}`} />
                {sidebarOpen && <span>Settings</span>}
              </Link>
            )}
          </nav>

          {/* Sidebar Footer - Org switcher + user */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <OrgSwitcher />
            <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
              {sidebarOpen ? (
                <>
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={`${user.firstName} ${user.lastName}`} className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary-600 dark:bg-primary-500 flex items-center justify-center text-white font-medium flex-shrink-0">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
                    title="Logout"
                  >
                    <FiLogOut className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-200"
                  title="Logout"
                >
                  <FiLogOut className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)}>
            <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"></div>
            <aside
              className="fixed top-16 left-0 bottom-0 w-64 bg-white dark:bg-gray-800 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <nav className="mt-5 px-2 space-y-1 flex-1 overflow-y-auto">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
                {hasPermission('users.view') && (
                  <Link
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      location.pathname === '/settings'
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <FiSettings className="h-5 w-5 mr-3" />
                    <span>Settings</span>
                  </Link>
                )}
              </nav>

              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <OrgSwitcher compact />
                <div className="flex items-center space-x-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-primary-600 dark:bg-primary-500 flex items-center justify-center text-white font-medium">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                >
                  <FiLogOut className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className={`flex-1 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'} transition-all duration-300`}>
          <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-4rem)]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
